import { Box, Flex, H3 } from '@ouster/webviz-react-components';

import { useMemo } from 'react';
import { MergerAffectedUsersResponse } from 'src/components/adminDashboard/types';

import { roleColorMap } from '../constants';
import { MergeUserPreview } from '../types';
import { buildRowsFromResponse } from '../utils';

const ROLE_PRIORITY: Record<MergeUserPreview['finalRole'], number> = {
  SUPER_ADMIN: -1,
  ADMIN: 0,
  SUPPORT: 1,
  BASIC: 2,
};

const sortUsersByOriginAndRole = (
  targetOrgName: string,
  first: MergeUserPreview,
  second: MergeUserPreview
): number => {
  const firstIsTarget = first.origin === targetOrgName;
  const secondIsTarget = second.origin === targetOrgName;

  if (firstIsTarget !== secondIsTarget) {
    return firstIsTarget ? -1 : 1;
  }

  const originDelta = first.origin.localeCompare(second.origin);
  if (originDelta !== 0) {
    return originDelta;
  }

  const roleDelta =
    ROLE_PRIORITY[first.finalRole] - ROLE_PRIORITY[second.finalRole];

  if (roleDelta !== 0) {
    return roleDelta;
  }

  return first.email.localeCompare(second.email);
};

interface UsersStepProps {
  initialAffectedUsersResponse?: MergerAffectedUsersResponse | null;
  isLoading?: boolean;
}

export default function UsersStep({
  initialAffectedUsersResponse = null,
  isLoading = false,
}: UsersStepProps) {
  const userPreviewRows = useMemo<MergeUserPreview[]>(
    () =>
      initialAffectedUsersResponse
        ? buildRowsFromResponse(initialAffectedUsersResponse).sort((a, b) =>
            sortUsersByOriginAndRole(
              initialAffectedUsersResponse.target.org_name,
              a,
              b
            )
          )
        : [],
    [initialAffectedUsersResponse]
  );

  return (
    <Box
      data-testid="merge-step-users"
      display="grid"
      rowGap={4}
      h="50vh"
      alignContent="start"
    >
      <Box>
        <H3 fontSize={26} mb={2}>
          User Preview
        </H3>
        <Box color="gray.300" fontSize={14} mb={4}>
          Roles from survivor are honored; source users are provisioned as basic
          members.
        </Box>
      </Box>

      <Box
        border="1px solid"
        borderColor="whiteAlpha.200"
        borderRadius={4}
        overflow="auto"
      >
        <Flex
          px={4}
          py={3}
          bg="whiteAlpha.100"
          color="gray.300"
          fontWeight={600}
          fontSize={14}
          textTransform="uppercase"
        >
          <Box flex={3}>User Email</Box>
          <Box flex={2}>Origin</Box>
          <Box flex={1}>Origin Role</Box>
          <Box flex={1}>Final Role</Box>
        </Flex>
        <Box overflowY="auto">
          {userPreviewRows.map((row) => (
            <Flex
              key={`${row.email}-${row.origin}`}
              px={4}
              py={3}
              fontSize={14}
              borderTop="1px solid"
              borderColor="whiteAlpha.100"
              alignItems="center"
            >
              <Box flex={3}>{row.email}</Box>
              <Box flex={2} color="gray.300">
                {row.origin}
              </Box>
              <Box
                flex={1}
                color="gray.300"
                fontSize={12}
                textTransform="uppercase"
                data-testid="merge-user-origin-role"
              >
                {row.originRole}
              </Box>
              <Box flex={1} fontSize={12}>
                <Box
                  px={2}
                  py={1}
                  borderRadius={4}
                  bg={roleColorMap[row.finalRole]}
                  textTransform="uppercase"
                  fontWeight={600}
                  width="80px"
                  textAlign="center"
                  data-testid="merge-user-final-role"
                >
                  {row.finalRole}
                </Box>
              </Box>
            </Flex>
          ))}
          {isLoading ? (
            <Box px={4} py={8} color="gray.400">
              Loading users…
            </Box>
          ) : userPreviewRows.length === 0 ? (
            <Box px={4} py={8} color="gray.400">
              No users found.
            </Box>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
}
